
import React, { useState, useEffect, useRef } from "react";
import { X, ChevronDown } from "lucide-react";
import Cookies from "js-cookie";
import api from "../chat/api";
import toast from "react-hot-toast";
import { markOnboarding } from "../../onboarding/onboardingStore";

const InviteTeamModal = ({ isOpen, onClose, onInviteSuccess }) => {
  const [emailInput, setEmailInput] = useState("");
  const [message, setMessage] = useState("");
  const [invitedPeople, setInvitedPeople] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [selectedRole, setSelectedRole] = useState("reviewer");
  const dropdownRef = useRef(null);

  // Invite into the active workspace (selected_org_id), kept in sync with the
  // global sidebar workspace switcher.
  const organizationId = Cookies.get("selected_org_id");

  // Reset form fields when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEmailInput("");
      setMessage("");
      setInvitedPeople([]);
      setIsSending(false);
      setShowRoleDropdown(false);
      setSelectedRole("reviewer");
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowRoleDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Handle email input change
  const handleEmailInputChange = (e) => {
    const value = e.target.value;
    setEmailInput(value);
  };

  // Handle role selection
  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setShowRoleDropdown(false);
  };

  // Handle Enter key press
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && emailInput.trim()) {
      e.preventDefault();
      const email = emailInput.trim();
      
      // Validate email format
      if (emailRegex.test(email)) {
        // Check if email is not already in the list
        if (!invitedPeople.find(person => person.email === email)) {
          setInvitedPeople([...invitedPeople, { email, role: selectedRole }]);
        }
        // Clear the input field and reset role
        setEmailInput("");
        setSelectedRole("reviewer");
        setShowRoleDropdown(false);
      } else {
        toast.error("Please enter a valid email address");
      }
    }
  };

  // Remove person from invited list
  const removePerson = (emailToRemove) => {
    setInvitedPeople(invitedPeople.filter((person) => person.email !== emailToRemove));
  };

  const handleSendInvite = async () => {
    if (invitedPeople.length === 0) {
      toast.error("Please add at least one email address");
      return;
    }

    if (!organizationId) {
      toast.error("Organization ID not found in URL");
      return;
    }

    const token = Cookies.get("access_token");
    if (!token) {
      toast.error("No authentication token available");
      return;
    }

    setIsSending(true);

    try {
      const emails = invitedPeople.map(person => person.email);
      const roles = invitedPeople.map(person => person.role);

      const requestBody = {
        organization_id: parseInt(organizationId), // Ensure "21" is sent as number 21
        emails: emails,
        role: roles,
        message: message.trim() || ""
      };

      const response = await api.post(
        "/api/v1/org/invites/",
        requestBody
      );

      if (response.status === 200 || response.status === 201) {
        toast.success("Invitation sent successfully!");
        markOnboarding("invite");
        
        // Wait 2 seconds before closing modal and refreshing
        setTimeout(() => {
          setEmailInput("");
          setMessage("");
          setInvitedPeople([]);
          setIsSending(false);
          onClose();
          
          if (onInviteSuccess) {
            onInviteSuccess();
          }
        }, 2000);
      }
    } catch (error) {
      console.error("Error sending invites:", error);
      setIsSending(false);

      if (error.response) {
        const { data, status } = error.response;
        
        // 1. Check for the specific "emails" error array first
        if (data.emails && Array.isArray(data.emails)) {
          // Join the array in case there are multiple error messages
          toast.error(data.emails.join(" ")); 
        } 
        // 2. Then check for a generic "message" string
        else if (data.message) {
          toast.error(data.message);
        } 
        // 3. Fallback to status codes
        else if (status === 400) {
          toast.error("Bad request. Please check details.");
        } else if (status === 401) {
          toast.error("Session expired. Please login again.");
        } else if (status === 403) {
          toast.error("You don't have permission to send invites.");
        } else if (status === 404) {
          toast.error("Organization not found.");
        } else {
          toast.error("An error occurred while sending invites.");
        }
      } else {
        toast.error("Failed to send invites.");
      }

      setTimeout(() => {
        onClose();
      }, 1000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm font-outfit">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="p-6 pb-0">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-[#18101C]">Invite Team Members</h2>
              <p className="text-[#52525B] text-sm mt-1">
                Invite people via email to join your team and start collaborating.
              </p>
            </div>
            <button 
              onClick={onClose}
              disabled={isSending}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors -mt-1 -mr-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="m-6 p-6 border border-gray-200 rounded-2xl space-y-6">
          
          {/* Email Input */}
          <div className="space-y-2">
            <label className="text-[15px] font-semibold !text-[#3F3F46]">
              Email Address
            </label>
            <div className="relative" ref={dropdownRef}>
              <div className="relative flex items-center">
                <input
                  type="text"
                  value={emailInput}
                  onChange={handleEmailInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Add email to invite"
                  disabled={isSending}
                  className="w-full px-4 pr-32 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C95EBE]/20 focus:border-[#C95EBE] transition-colors box-border disabled:bg-gray-50 disabled:cursor-not-allowed"
                  style={{ height: '46px', fontSize: '14px', lineHeight: '20px' }}
                />
                {emailInput.trim().length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowRoleDropdown(!showRoleDropdown)}
                    disabled={isSending}
                    className="absolute right-2 flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1)}
                    <ChevronDown size={16} className={`transition-transform ${showRoleDropdown ? 'rotate-180' : ''}`} />
                  </button>
                )}
              </div>
              
              {showRoleDropdown && emailInput.trim().length > 0 && (
                <div className="absolute right-0 mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-10 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => handleRoleSelect("admin")}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors"
                  >
                    Admin
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRoleSelect("reviewer")}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors"
                  >
                    Reviewer
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRoleSelect("editor")}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors"
                  >
                    Editor
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Invited List */}
          <div className="space-y-2">
            <label className="text-[15px] font-semibold !text-[#3F3F46] flex justify-between">
              <span>People Invited ({invitedPeople.length})</span>
            </label>
            <div className="w-full h-[120px] px-4 py-3 border border-gray-200 rounded-lg bg-white text-sm overflow-y-auto">
              {invitedPeople.length === 0 ? (
                <span className="text-gray-400">No one invited currently</span>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {invitedPeople.map((person, index) => (
                    <span 
                      key={index} 
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-[#BB42AE4D] text-[#18101C] border border-[#C95EBE]/20 h-fit"
                    >
                      {person.email}
                      <span className="text-xs font-medium text-[#C95EBE]">
                        ({person.role})
                      </span>
                      <button
                        onClick={() => removePerson(person.email)}
                        disabled={isSending}
                        className="hover:bg-[#C95EBE]/20 rounded-full p-0.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex gap-3 items-end">
            <div className="flex-grow">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Add a message (optional)"
                disabled={isSending}
                className="w-full px-4 bg-[#F5F5F5] border border-transparent rounded-lg focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#C95EBE]/20 focus:border-[#C95EBE] transition-colors box-border disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ height: '42px', fontSize: '14px', lineHeight: '20px' }}
              />
            </div>
            <button
              onClick={handleSendInvite}
              disabled={invitedPeople.length === 0 || isSending}
              className={`px-6 rounded-lg !text-white font-medium text-sm whitespace-nowrap transition-all box-border ${
                invitedPeople.length > 0 && !isSending
                  ? "bg-[#C95EBE] hover:bg-[#b04da6] shadow-sm hover:shadow" 
                  : "bg-[#C95EBE]/50 cursor-not-allowed"
              }`}
              style={{ height: '42px' }}
            >
              {isSending ? "Sending..." : "Send Invite"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteTeamModal;
